import React, { useEffect, useMemo, useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";
import { useInView } from "../../hooks/useInView";
import StripeBillingGate from "../StripeBillingGate";

// Security constants
const MAX_DESCRIPTION_LENGTH = 200;
const SUBMISSION_COOLDOWN = 1000; // 1 second between API calls

// Input sanitization function
const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Remove dangerous patterns
  sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
  sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
  sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
  sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
  sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
  
  // Limit length
  if (sanitized.length > MAX_DESCRIPTION_LENGTH) {
    sanitized = sanitized.substring(0, MAX_DESCRIPTION_LENGTH) + '...';
  }
  
  return sanitized.trim();
};

// Validate URL parameters
const validateStudioId = (studioId: string | null): boolean => {
  if (!studioId || typeof studioId !== 'string') return false;
  // UUID format validation
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(studioId);
};

const validatePeriod = (period: string): boolean => {
  const validPeriods = ['month', 'last-month', 'all-time'];
  return validPeriods.includes(period);
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

type PaymentCategory = 'membership' | 'store' | 'event' | 'other';

type AnalyticsRow = { 
  description: string; 
  total: number; 
  count: number;
  category?: PaymentCategory;
  grossTotal?: number;
  refundedTotal?: number;
};

const CATEGORY_SECTIONS: { key: PaymentCategory; label: string }[] = [
  { key: 'membership', label: 'Memberships' },
  { key: 'store', label: 'Store' },
  { key: 'event', label: 'Events' },
  { key: 'other', label: 'Other' },
];

export default function PaymentsByDescriptionCard({
  studioId,
  isStripeConnected,
  canUseStripeBilling = true,
}: {
  studioId: string | null;
  isStripeConnected: boolean | null;
  canUseStripeBilling?: boolean;
}) {
  const { getAuthToken } = useAuth();
  const { ref, inView } = useInView();
  const [period, setPeriod] = useState<"month" | "last-month" | "all-time">(
    "month"
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [dataByPeriod, setDataByPeriod] = useState<
    Partial<Record<"month" | "last-month" | "all-time", AnalyticsRow[]>>
  >({});
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  useEffect(() => {
    if (!canUseStripeBilling || isStripeConnected === false) {
      setLoading(false);
    }
  }, [canUseStripeBilling, isStripeConnected]);

  // Fetch data when visible and missing for selected period
  useEffect(() => {
    const fetchData = async () => {
      if (!inView || !studioId || !canUseStripeBilling || !isStripeConnected) return;
      if (dataByPeriod[period]) return; // already have
      
      // Security validations
      if (!validateStudioId(studioId)) {
        logSecurityEvent('INVALID_STUDIO_ID', { studioId });
        return;
      }
      
      if (!validatePeriod(period)) {
        logSecurityEvent('INVALID_PERIOD_PARAMETER', { period });
        return;
      }
      
      // Rate limiting
      const now = Date.now();
      if (now - lastFetchTime < SUBMISSION_COOLDOWN) {
        return;
      }
      
      const authToken = getAuthToken();
      if (!authToken) return;
      
      setLoading(true);
      setLastFetchTime(now);
      
      try {
        // Use encodeURIComponent for URL parameters
        const encodedStudioId = encodeURIComponent(studioId);
        const encodedPeriod = encodeURIComponent(period);
        
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/analytics/by-description/${encodedStudioId}?period=${encodedPeriod}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        
        if (res.ok) {
          const json = await res.json();
          // Sanitize the analytics data
          const sanitizedAnalytics = (json.analytics || []).map((item: AnalyticsRow) => ({
            ...item,
            description: sanitizeInput(item.description)
          }));
          setDataByPeriod((prev) => ({ ...prev, [period]: sanitizedAnalytics }));
        } else {
          logSecurityEvent('API_REQUEST_FAILED', { 
            status: res.status, 
            statusText: res.statusText,
            endpoint: 'analytics/by-description'
          });
        }
      } catch (error) {
        logSecurityEvent('API_REQUEST_ERROR', { 
          error: error instanceof Error ? error.message : 'Unknown error',
          endpoint: 'analytics/by-description'
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [inView, studioId, canUseStripeBilling, isStripeConnected, period, getAuthToken, dataByPeriod, lastFetchTime]);

  const currentData: AnalyticsRow[] = useMemo(() => {
    return dataByPeriod[period] || [];
  }, [dataByPeriod, period]);

  // Filter data based on search term
  const filteredData: AnalyticsRow[] = useMemo(() => {
    if (!searchTerm.trim()) {
      return currentData;
    }
    const sanitizedSearch = sanitizeInput(searchTerm.toLowerCase());
    return currentData.filter((payment) =>
      payment.description.toLowerCase().includes(sanitizedSearch)
    );
  }, [currentData, searchTerm]);

  const [isExpanded, setIsExpanded] = useState(false);
  const displayedPayments = isExpanded ? filteredData : filteredData.slice(0, 10);
  const hasMore = filteredData.length > 10;
  const total = filteredData.reduce((sum, payment) => sum + payment.total, 0);
  const totalRefunded = filteredData.reduce((sum, payment) => sum + (payment.refundedTotal ?? 0), 0);

  // Group the visible rows under section headings; subtotals cover the whole filtered list
  const sections = CATEGORY_SECTIONS.map((section) => ({
    ...section,
    rows: displayedPayments.filter((p) => (p.category ?? 'other') === section.key),
    subtotal: filteredData
      .filter((p) => (p.category ?? 'other') === section.key)
      .reduce((sum, p) => sum + p.total, 0),
  })).filter((section) => section.rows.length > 0);
  return (
    <div ref={ref} className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 px-4 py-5 border-2 border-blue-100 dark:border-blue-800 shadow-md rounded-xl sm:px-6 sm:py-6 hover:shadow-lg hover:-translate-y-1 transition">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
          Total Payments
        </h3>
        <div className="flex items-center gap-2">
          {/* Search input */}
          <div className="relative flex-[0.7]">
            <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                const sanitized = sanitizeInput(e.target.value);
                setSearchTerm(sanitized);
              }}
              placeholder="Search payments..."
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md pl-8 pr-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
            />
          </div>
          {/* Period dropdown */}
          <div className="relative flex-[0.3]">
            <select
              value={period}
              onChange={(e) => {
                const newPeriod = e.target.value as "month" | "last-month" | "all-time";
                if (validatePeriod(newPeriod)) {
                  setPeriod(newPeriod);
                  setIsExpanded(false); // Reset expanded state when period changes
                } else {
                  logSecurityEvent('INVALID_PERIOD_SELECTION', { value: e.target.value });
                }
              }}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md pl-2 pr-8 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer w-full"
              style={{
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: 'none'
              }}
            >
              <option value="month">This Month</option>
              <option value="last-month">Last Month</option>
              <option value="all-time">All Time</option>
            </select>
            {/* Custom dropdown arrow */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 min-h-64 max-h-96 flex flex-col">
        {isStripeConnected === null && canUseStripeBilling ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Preparing payment analytics...</div>
            </div>
          </div>
        ) : !canUseStripeBilling || isStripeConnected === false ? (
          <StripeBillingGate
            canUseStripeBilling={canUseStripeBilling}
            isStripeConnected={isStripeConnected}
            variant="card"
            className="py-8"
          />
        ) : (!inView || loading) && !currentData.length ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="h-5 w-5 bg-green-200 dark:bg-green-600 rounded-full animate-spin"></div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Loading payment data...
              </div>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400 dark:text-gray-500">
            {searchTerm ? (
              <>
                <div className="text-4xl mb-2">🔍</div>
                <p className="text-sm italic">No payments match your search</p>
              </>
            ) : (
              <>
            <div className="text-4xl mb-2">🧾</div>
            <p className="text-sm italic">No payment data</p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-2 mb-4 overflow-y-auto min-h-0">
              {sections.map((section) => (
                <div key={section.key} className="space-y-1.5">
                  <div className="flex items-center justify-between pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <span>{section.label}</span>
                    <span>${section.subtotal.toFixed(2)}</span>
                  </div>
                  {section.rows.map((payment) => (
                    <div
                      key={`${section.key}:${payment.description}`}
                      className="flex justify-between items-start text-sm border-b border-dotted border-gray-300 dark:border-gray-600 pb-1"
                    >
                      <div className="flex-1 pr-2 min-w-0">
                        <div className="text-gray-700 dark:text-gray-300 font-medium truncate" title={payment.description}>
                          {payment.description}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {payment.count} payment{payment.count === 1 ? '' : 's'}
                          {(payment.refundedTotal ?? 0) > 0 && (
                            <span className="text-red-600 dark:text-red-400">
                              {' '}· ${payment.refundedTotal!.toFixed(2)} refunded
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-gray-900 dark:text-white font-semibold">
                        ${payment.total.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}

              {hasMore && !isExpanded && (
                <button
                  onClick={() => setIsExpanded(true)}
                  className="w-full text-center py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium border border-indigo-200 dark:border-indigo-700 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                >
                  Show {filteredData.length - 10} more items...
                </button>
              )}

              {hasMore && isExpanded && (
                <button
                  onClick={() => setIsExpanded(false)}
                  className="w-full text-center py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 font-medium border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Show less
                </button>
              )}
            </div>

            <div className="border-t-2 border-gray-800 dark:border-gray-200 pt-2 flex-shrink-0 space-y-1">
              {/* Refunds are already taken out of TOTAL */}
              {totalRefunded > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Refunds
                  </span>
                  <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                    −${totalRefunded.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-800 dark:text-gray-200">
                  TOTAL
                </span>
                <span className="text-xl font-bold text-green-600 dark:text-green-400">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}